import type { MapResponse, PlayerProfile } from "@monekineko/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, api, login } from "../api.js";

// Состояние игрока: профиль, карта и пинг игрового времени. Всё остальное
// живёт в экранах — состояние тут нарочно маленькое.

interface SessionState {
  profile: PlayerProfile | null;
  map: MapResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  setProfile: (profile: PlayerProfile) => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [map, setMap] = useState<MapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sessionId = useRef<string | undefined>(undefined);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [nextProfile, nextMap] = await Promise.all([api.me(), api.map()]);
      setProfile(nextProfile);
      setMap(nextMap);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось связаться с сервером");
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      setProfile(await api.me());
    } catch {
      // профиль обновится со следующей попыткой
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const next = await login();
        if (cancelled) return;
        setProfile(next);
        setMap(await api.map());
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : "Игра открывается из Telegram — запусти её кнопкой в боте",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Игровое время считает сервер, клиент только подаёт признаки жизни
  useEffect(() => {
    if (!profile) return;
    let stopped = false;

    const beat = async (): Promise<void> => {
      if (stopped || document.hidden) return;
      try {
        const result = await api.ping(sessionId.current);
        sessionId.current = result.sessionId;
      } catch {
        // пинг не критичен
      }
    };

    void beat();
    const timer = setInterval(() => void beat(), 30_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [profile]);

  const value = useMemo<SessionState>(
    () => ({ profile, map, loading, error, reload, refreshProfile, setProfile }),
    [profile, map, loading, error, reload, refreshProfile],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession вне SessionProvider");
  return value;
}
